import { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon, CheckCircle2Icon } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { FOOTBALL_PRESETS } from '@/constants/FootballPresets'
import type { PresetSystem, PresetVocabularyEntry } from '@/constants/FootballPresets'
import type { PresetImportResult } from '@/services/PresetImporter'
import { importPresetVocabulary } from '@/services/PresetImporter'
import type { VocabularyCategory } from '@/db/Types'
import './onboarding-wizard.css'

type WizardStep = 1 | 2 | 3

interface OnboardingWizardProps {
	isOpen: boolean
	onClose: () => void
	playbookId: string
	onImportComplete?: () => void
}

const CATEGORY_LABELS: Record<VocabularyCategory, string> = {
	formation: 'Formations',
	modifier: 'Modifiers',
	route: 'Route Concepts',
	blocking: 'Run Schemes',
	motion: 'Motions',
	protection: 'Protections',
}

/** Consistent display order for categories */
const CATEGORY_ORDER: VocabularyCategory[] = [
	'formation', 'modifier', 'route', 'blocking', 'motion', 'protection',
]

const STEP_TITLES: Record<WizardStep, string> = {
	1: 'Choose a System',
	2: 'Review Vocabulary',
	3: 'All Set!',
}

function StepIndicator({ currentStep }: { currentStep: WizardStep }) {
	return (
		<span className="onboarding-wizard__steps">
			{([1, 2, 3] as WizardStep[]).map(step => {
				const isActive = step === currentStep
				const isCompleted = step < currentStep
				return (
					<span
						key={step}
						className={`onboarding-wizard__step-dot${isActive ? ' onboarding-wizard__step-dot--active' : ''}${isCompleted ? ' onboarding-wizard__step-dot--completed' : ''}`}
					/>
				)
			})}
			<span className="onboarding-wizard__step-label">
				Step {currentStep} of 3
			</span>
		</span>
	)
}

interface CategoryGroupProps {
	category: VocabularyCategory
	entries: PresetVocabularyEntry[]
	selectedIndexes: Set<number>
	allIndexes: number[]
	onToggleCategory: (indexes: number[], checked: boolean) => void
	onToggleEntry: (index: number, checked: boolean) => void
}

function CategoryGroup({
	category,
	entries,
	selectedIndexes,
	allIndexes,
	onToggleCategory,
	onToggleEntry,
}: CategoryGroupProps) {
	const [isExpanded, setIsExpanded] = useState(true)

	const selectedCount = allIndexes.filter(i => selectedIndexes.has(i)).length
	const allSelected = selectedCount === allIndexes.length
	const someSelected = selectedCount > 0 && !allSelected

	return (
		<div className="onboarding-wizard__category-group">
			<div
				className="onboarding-wizard__category-header"
				onClick={() => setIsExpanded(prev => !prev)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						setIsExpanded(prev => !prev)
					}
				}}
			>
				<Checkbox
					checked={allSelected ? true : someSelected ? 'indeterminate' : false}
					onCheckedChange={(checked) => {
						onToggleCategory(allIndexes, !!checked)
					}}
					onClick={(e) => e.stopPropagation()}
					aria-label={`Select all ${CATEGORY_LABELS[category]}`}
				/>
				<span className="onboarding-wizard__category-name">
					{CATEGORY_LABELS[category]}
				</span>
				<span className="onboarding-wizard__count-badge">
					{selectedCount} / {entries.length}
				</span>
				<ChevronDownIcon
					className={`onboarding-wizard__expand-icon${isExpanded ? ' onboarding-wizard__expand-icon--open' : ''}`}
				/>
			</div>
			{isExpanded && (
				<div className="onboarding-wizard__entry-list">
					{entries.map((entry, localIndex) => {
						const globalIndex = allIndexes[localIndex]!
						return (
							<label key={globalIndex} className="onboarding-wizard__entry">
								<Checkbox
									checked={selectedIndexes.has(globalIndex)}
									onCheckedChange={(checked) => {
										onToggleEntry(globalIndex, !!checked)
									}}
									aria-label={`Select ${entry.displayName}`}
								/>
								<span className="onboarding-wizard__entry-code">{entry.code}</span>
								<span className="onboarding-wizard__entry-name">{entry.displayName}</span>
							</label>
						)
					})}
				</div>
			)}
		</div>
	)
}

export function OnboardingWizard({ isOpen, onClose, playbookId, onImportComplete }: OnboardingWizardProps) {
	const [step, setStep] = useState<WizardStep>(1)
	const [selectedSystem, setSelectedSystem] = useState<PresetSystem | null>(null)
	const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set())
	const [isImporting, setIsImporting] = useState(false)
	const [importResult, setImportResult] = useState<PresetImportResult | null>(null)

	// Reset state when dialog opens
	useEffect(() => {
		if (isOpen) {
			setStep(1)
			setSelectedSystem(null)
			setSelectedEntries(new Set())
			setIsImporting(false)
			setImportResult(null)
		}
	}, [isOpen])

	// Group vocabulary by category for the selected system
	const categorizedVocabulary = useMemo(() => {
		if (!selectedSystem) return []

		const groups: { category: VocabularyCategory; entries: PresetVocabularyEntry[]; indexes: number[] }[] = []

		for (const category of CATEGORY_ORDER) {
			const entries: PresetVocabularyEntry[] = []
			const indexes: number[] = []

			selectedSystem.vocabulary.forEach((entry, index) => {
				if (entry.category === category) {
					entries.push(entry)
					indexes.push(index)
				}
			})

			if (entries.length > 0) {
				groups.push({ category, entries, indexes })
			}
		}

		return groups
	}, [selectedSystem])

	function handleSystemSelect(system: PresetSystem) {
		setSelectedSystem(system)
		setSelectedEntries(new Set(system.vocabulary.map((_, i) => i)))
	}

	function handleNext() {
		if (!selectedSystem) return

		if (selectedSystem.id === 'custom') {
			// Custom system skips vocabulary preview, go straight to import (which creates 0 entries)
			handleImport([])
		} else {
			setStep(2)
		}
	}

	function handleBack() {
		setStep(1)
	}

	function handleToggleCategory(indexes: number[], checked: boolean) {
		setSelectedEntries(prev => {
			const next = new Set(prev)
			for (const index of indexes) {
				if (checked) {
					next.add(index)
				} else {
					next.delete(index)
				}
			}
			return next
		})
	}

	function handleToggleEntry(index: number, checked: boolean) {
		setSelectedEntries(prev => {
			const next = new Set(prev)
			if (checked) {
				next.add(index)
			} else {
				next.delete(index)
			}
			return next
		})
	}

	async function handleImport(entriesToImport?: PresetVocabularyEntry[]) {
		if (!selectedSystem && !entriesToImport) return

		setIsImporting(true)

		const entries = entriesToImport ?? selectedSystem!.vocabulary.filter((_, i) => selectedEntries.has(i))

		try {
			const result = await importPresetVocabulary(playbookId, entries)
			setImportResult(result)
			setStep(3)
			onImportComplete?.()
		} catch (error) {
			setImportResult({
				entriesCreated: 0,
				skipped: 0,
				errors: [error instanceof Error ? error.message : 'Import failed. Please try again.'],
			})
			setStep(3)
		} finally {
			setIsImporting(false)
		}
	}

	function handleOpenChange(open: boolean) {
		if (!open && !isImporting) {
			onClose()
		}
	}

	const selectedCount = selectedEntries.size

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{STEP_TITLES[step]}</DialogTitle>
					<DialogDescription>
						<StepIndicator currentStep={step} />
					</DialogDescription>
				</DialogHeader>

				{/* Step 1: System Selector */}
				{step === 1 && (
					<>
						<div className="onboarding-wizard__systems" role="radiogroup" aria-label="Football system">
							{FOOTBALL_PRESETS.map(system => (
								<div
									key={system.id}
									className={`onboarding-wizard__system-card${selectedSystem?.id === system.id ? ' onboarding-wizard__system-card--selected' : ''}`}
									onClick={() => handleSystemSelect(system)}
									role="radio"
									aria-checked={selectedSystem?.id === system.id}
									tabIndex={0}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault()
											handleSystemSelect(system)
										}
									}}
								>
									<span className="onboarding-wizard__system-name">{system.name}</span>
									<span className="onboarding-wizard__system-description">{system.description}</span>
								</div>
							))}
						</div>
						<DialogFooter>
							<Button variant="ghost" onClick={onClose}>
								Cancel
							</Button>
							<Button
								variant="action"
								onClick={handleNext}
								disabled={!selectedSystem}
							>
								Next
							</Button>
						</DialogFooter>
					</>
				)}

				{/* Step 2: Vocabulary Preview */}
				{step === 2 && selectedSystem && (
					<>
						<p className="onboarding-wizard__selection-summary">
							{selectedCount} of {selectedSystem.vocabulary.length} entries selected
						</p>
						<div className="onboarding-wizard__vocabulary">
							{categorizedVocabulary.map(({ category, entries, indexes }) => (
								<CategoryGroup
									key={category}
									category={category}
									entries={entries}
									selectedIndexes={selectedEntries}
									allIndexes={indexes}
									onToggleCategory={handleToggleCategory}
									onToggleEntry={handleToggleEntry}
								/>
							))}
						</div>
						<DialogFooter>
							<Button variant="ghost" onClick={handleBack}>
								Back
							</Button>
							<Button
								variant="action"
								onClick={() => handleImport()}
								disabled={isImporting || selectedCount === 0}
							>
								{isImporting ? 'Importing...' : `Import ${selectedCount} Entries`}
							</Button>
						</DialogFooter>
					</>
				)}

				{/* Step 3: Import Confirmation */}
				{step === 3 && importResult && (
					<>
						<div className="onboarding-wizard__result">
							<CheckCircle2Icon className="onboarding-wizard__result-icon" />
							<p className="onboarding-wizard__result-title">
								{importResult.entriesCreated > 0
									? 'Vocabulary Imported!'
									: selectedSystem?.id === 'custom'
										? 'Ready to Go!'
										: 'No Entries Imported'}
							</p>
							<p className="onboarding-wizard__result-count">
								{importResult.entriesCreated > 0
									? `${importResult.entriesCreated} entries added to your vocabulary.`
									: selectedSystem?.id === 'custom'
										? 'You can add vocabulary as you create formations and concepts.'
										: 'No new entries were added.'}
								{importResult.skipped > 0 && ` ${importResult.skipped} already existed.`}
							</p>
							{importResult.errors.length > 0 && (
								<div className="onboarding-wizard__result-errors">
									{importResult.errors.map((error, i) => (
										<p key={i} className="onboarding-wizard__result-error-item">{error}</p>
									))}
								</div>
							)}
						</div>
						<DialogFooter>
							<Button variant="ghost" onClick={onClose}>
								Close
							</Button>
							<Button variant="action" onClick={onClose}>
								Get Started
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}
